/**
 * Insurance Estimates (Predeterminations) master modal.
 *
 * Shares VISUAL TREATMENT with InsuranceBreakdownMasterModal (purple
 * palette, 1400px sidebar+content layout, AntD Menu list, sticky action
 * stack, Collapse policy info, BreakdownFailureCard attention surface,
 * empty-state with icon + heading) for app-wide consistency.
 *
 * Intentionally does NOT mirror these breakdown-only concepts:
 *   - No "Default" hero card — predeterminations have no analog of a
 *     pinned default; each case from the portal is independent.
 *   - No "Other Breakdowns" vs default split — flat chronological list.
 *   - No 4 category cards (Preventative/Basic/Major/Orthodontics) —
 *     estimates are claim-level, not coverage-category-level.
 *   - No "Set as Default" / Recalculate flow.
 *   - No "Add Manually" / "Upload PDF" here: what the FTC got back on a
 *     requested estimate is recorded per submission in the results pane
 *     (RequestedEstimatesPanel), next to what the office asked for. The
 *     sidebar list holds AI-fetched cases only.
 *
 * Does have, unlike the breakdown modal:
 *   - A per-row DELETE (the backend reclaims the attached PDF and clears
 *     "New Estimate Available" when it was the policy's last estimate;
 *     auto-generated treatment plans are deliberately kept).
 *   - A full-size inline PDF viewer for "Simple Upload" cases, where the
 *     filed document IS the whole record. See EstimatePdfPreview.
 *
 * Reachable from two places:
 *   - patient row on the Patients board (no plan context)
 *   - treatment plan details page (defaultTreatmentPlanId may be set)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Menu, message, Modal, Popconfirm, Spin } from "antd";
import { FileSearchOutlined, CheckCircleOutlined, DeleteOutlined } from "@ant-design/icons";

import CarrierContactBar from "../shared/CarrierContactBar";
import CarrierPortalCredential from "../shared/CarrierPortalCredential";
import VerificationActionButton from "../shared/VerificationActionButton";
import useRequestComposer from "../../../InsuranceRequests/useRequestComposer";
import RequestedEstimatesPanel from "../shared/RequestedEstimatesPanel";
import PolicySelect from "../shared/PolicySelect";
import HandledByChip from "../shared/HandledByChip";
import SelectEstimateProvidersModal from "../shared/SelectEstimateProvidersModal";
import BreakdownFailureCard from "../Breakdown/BreakdownFailureCard";
import PolicyInformationCollapse from "../shared/PolicyInformationCollapse";
import PredeterminationDetails from "../Predetermination/PredeterminationDetails";

import PredeterminationUploadedFiles from "../Predetermination/PredeterminationUploadedFiles";
import EstimatePdfPreview from "./EstimatePdfPreview";
import EstimateNoteBlock from "./EstimateNoteBlock";

import { controller as rootController } from "../../../../controller";
import {
  carrierCodeFromPolicy,
  PREDETERMINATION_AI_METHODS,
  portalForCarrier,
  supportsAny,
} from "../../lib/carrierCapabilities";

const SOURCE_LABELS = {
  manual: "Manual Entry",
  pdf_upload: "PDF Upload",
  pdf_simple: "Simple Upload",
  ai_scraper: "AI Fetch",
  ai_phone_call: "AI Phone Call",
  auto: "Auto-Created",
};

function formatPlainDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString();
  } catch (e) {
    return null;
  }
}

function formatTimestamp(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return null;
  }
}

export default function InsuranceEstimatesMasterModal({
  showInsuranceEstimatesModal,
  setShowInsuranceEstimatesModal,
  patientPolicies,
  // Optional: pre-select this policy on open (else the first policy).
  defaultSelectedPolicyId,
  // Optional: pre-select this predetermination case id in the left list on open.
  defaultSelectedEntryId,
  patient,
  // Patient's own office (threaded for cross-office FTC/super-admin use); falls
  // back to selectedOffice for the provider picker + Show-login when absent.
  officeId: officeIdProp,
  officeIdentity,
  // The carrier list behind Policy Information's editable Carrier select.
  // Fetched once by InsuranceMasterModal and shared by every tab.
  carrierOptions,
  isLoadingCarriers,
  // True while policies are auto-fetched from the PMS for a no-policy patient;
  // shows a spinner in the Select-Policy sidebar until the fetch resolves.
  isFetchingPolicies,
  defaultTreatmentPlanId,
  predeterminationData,
  agentType,
  handleScraperAgentCall,
  checkPatientInsuranceEligibility,
  isAgentScrappingData,
  onRequestAddPolicy,
  onPoliciesChanged,
  onNoteSaved,
  // Viewer mode — see InsuranceMasterModal. Hides the carrier contact bar, the
  // saved portal login and the Request / Upload-PDF rail.
  hideVerificationTools = false,
  // When true, render only the inner two-pane body (no own <Modal> wrapper,
  // no Close bar) so InsuranceMasterModal can host it as a tab.
  embedded = false,
}) {
  const patientId = patient && patient.id;

  const [selectedPolicyID, setSelectedPolicyID] = useState(undefined);
  const [casesList, setCasesList] = useState([]);
  // Provider-account picker for estimate fetch (an estimate only appears under
  // the submitting provider's account, so the office may have several accounts).
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [estimateAccounts, setEstimateAccounts] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(undefined);
  const [capabilityMatrix, setCapabilityMatrix] = useState(null);

  const [policyCollapseActiveKey, setPolicyCollapseActiveKey] = useState([]);
  const [policyOverrides, setPolicyOverrides] = useState({});

  const [refreshTick, setRefreshTick] = useState(0);
  const [deletingCaseId, setDeletingCaseId] = useState(undefined);

  // See the Breakdown modal: one shared request flow, opened over this modal. A
  // request sent from here re-reads the submissions panel in the results pane.
  const handleComposerSubmitted = useCallback(function () {
    setRefreshTick((n) => n + 1);
  }, []);
  const { openComposer, composer } = useRequestComposer({ overModal: true, onSubmitted: handleComposerSubmitted });

  useEffect(function () {
    let cancelled = false;
    rootController
      .getCarrierCapabilities()
      .then((matrix) => {
        if (!cancelled) setCapabilityMatrix(matrix);
      })
      .catch((err) => console.error("Failed to load carrier capabilities", err));
    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(
    function () {
      if (!showInsuranceEstimatesModal) return;
      if (selectedPolicyID) return;
      if (Array.isArray(patientPolicies) && patientPolicies.length > 0) {
        // Honor a caller-requested policy (e.g. an FTC estimate chip) when the
        // patient actually holds it, else fall back to the first policy.
        const preferred =
          defaultSelectedPolicyId != null &&
          patientPolicies.some((p) => p.id === defaultSelectedPolicyId)
            ? defaultSelectedPolicyId
            : patientPolicies[0].id;
        setSelectedPolicyID(preferred);
      }
    },
    [showInsuranceEstimatesModal, patientPolicies, selectedPolicyID, defaultSelectedPolicyId],
  );

  const selectedPolicy = useMemo(
    function () {
      if (!selectedPolicyID || !Array.isArray(patientPolicies)) return undefined;
      const base = patientPolicies.find((p) => p.id === selectedPolicyID);
      if (!base) return undefined;
      const ov = policyOverrides[base.id];
      return ov ? { ...base, ...ov } : base;
    },
    [selectedPolicyID, patientPolicies, policyOverrides],
  );

  const selectedCarrierCode = useMemo(
    function () {
      return carrierCodeFromPolicy(selectedPolicy) || "";
    },
    [selectedPolicy],
  );

  const aiSupported = useMemo(
    function () {
      if (!capabilityMatrix || !selectedCarrierCode) return false;
      return supportsAny(capabilityMatrix, selectedCarrierCode, "predetermination", PREDETERMINATION_AI_METHODS);
    },
    [capabilityMatrix, selectedCarrierCode],
  );

  const aiDisableReason = useMemo(
    function () {
      if (!capabilityMatrix) return null;
      if (!selectedPolicy) return "Select a policy first.";
      if (!aiSupported) return "This carrier doesn't support any AI fetch method for predeterminations.";
      return null;
    },
    [capabilityMatrix, selectedPolicy, aiSupported],
  );
  const aiTooltip = aiDisableReason || "AI will fetch predeterminations instantly.";

  const loadCases = useCallback(
    async function loadCasesInner() {
      if (!patientId) return;
      try {
        setIsLoadingList(true);
        const casesRes = await rootController.listPatientPredeterminationCases({
          patientId,
          policyId: selectedPolicyID,
          treatmentPlanId: defaultTreatmentPlanId,
        });
        setCasesList(casesRes.status < 250 && Array.isArray(casesRes.json) ? casesRes.json : []);
      } catch (err) {
        console.error("Failed to load predetermination cases", err);
        setCasesList([]);
      } finally {
        setIsLoadingList(false);
      }
    },
    [patientId, selectedPolicyID, defaultTreatmentPlanId],
  );

  useEffect(
    function () {
      if (!showInsuranceEstimatesModal) return;
      loadCases();
    },
    [showInsuranceEstimatesModal, loadCases, refreshTick],
  );

  // Auto-select an entry once the list loads (the Estimate tab historically left
  // the right pane empty until the user clicked). On the first load prefer the
  // deep-linked `defaultSelectedEntryId` (from the "View Estimate" button);
  // otherwise, and on later policy switches, fall back to the newest case. Never
  // overrides an existing selection (a user's click, or a prior auto-select).
  const initialCaseConsumedRef = useRef(false);
  useEffect(
    function () {
      if (!Array.isArray(casesList) || casesList.length === 0) return;
      if (selectedCaseId) return;
      let target;
      if (
        !initialCaseConsumedRef.current &&
        defaultSelectedEntryId != null &&
        casesList.some((c) => c.id === defaultSelectedEntryId)
      ) {
        target = defaultSelectedEntryId;
      } else {
        target = casesList[0].id;
      }
      initialCaseConsumedRef.current = true;
      setSelectedCaseId(target);
    },
    [casesList, defaultSelectedEntryId, selectedCaseId],
  );

  useEffect(
    function () {
      if (agentType === "predetermination" && predeterminationData !== undefined) {
        setRefreshTick((n) => n + 1);
      }
    },
    [agentType, predeterminationData],
  );

  // Dispatch the predetermination fetch for the selected policy, optionally
  // fanned out across the chosen provider accounts (one scrape each). Prefers
  // the direct hook (so we keep the already-selected policy and pass
  // provider_ids); falls back to the legacy handleScraperAgentCall prop.
  function dispatchPredetermination(providerIds) {
    setSelectedCaseId(undefined);
    if (typeof checkPatientInsuranceEligibility === "function") {
      checkPatientInsuranceEligibility("predetermination", selectedPolicyID, undefined, providerIds || []);
    } else if (typeof handleScraperAgentCall === "function") {
      handleScraperAgentCall("predetermination");
    }
  }

  async function handleAIVerification() {
    if (!selectedPolicy) {
      message.warning("Select an insurance policy first.");
      return;
    }
    if (!aiSupported) return;

    // Find this carrier's portal accounts so the user can choose which
    // provider's account to fetch under. If we can't, just dispatch normally
    // (the backend uses the office default account).
    const portal = portalForCarrier(selectedCarrierCode);
    // Prefer the patient's own office (threaded) so a cross-office FTC user picks
    // the right office's provider accounts, not the header office's.
    const officeId = officeIdProp || (typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem("selectedOffice")
      : null);
    if (!portal || !officeId) {
      dispatchPredetermination([]);
      return;
    }

    setPickerBusy(true);
    try {
      const res = await rootController.listPortalCredentials(officeId);
      const all = (res && res.credentials) || [];
      const accounts = all.filter(function (c) {
        return c.portal_code === portal && c.provider;
      });
      if (accounts.length > 1) {
        setEstimateAccounts(accounts);
        setShowProviderPicker(true);
        return;
      }
      // 0 accounts → default account; exactly 1 → that provider's account.
      const ids = accounts.length === 1 ? [accounts[0].provider.id] : [];
      dispatchPredetermination(ids);
    } catch (e) {
      // Listing accounts is best-effort — fall back to the default account.
      dispatchPredetermination([]);
    } finally {
      setPickerBusy(false);
    }
  }


  async function handleDeleteCase(caseId) {
    try {
      setDeletingCaseId(caseId);
      const res = await rootController.deletePredeterminationCase(caseId);
      if (res && res.status < 250) {
        message.success("Estimate deleted");
        // Clear the selection BEFORE refreshing: the auto-select effect only
        // picks a row when selectedCaseId is falsy, so leaving a deleted id in
        // place would strand the right pane on a case that no longer exists.
        setSelectedCaseId(undefined);
        setRefreshTick((n) => n + 1);
      } else {
        message.error((res && res.json && (res.json.error || res.json.detail)) || "Couldn't delete the estimate.");
      }
    } catch (err) {
      console.error(err);
      message.error("Couldn't delete the estimate.");
    } finally {
      setDeletingCaseId(undefined);
    }
  }

  const selectedCase = useMemo(
    function () {
      if (!selectedCaseId) return undefined;
      return casesList.find((c) => c.id === selectedCaseId);
    },
    [selectedCaseId, casesList],
  );

  const renderSidebar = () => (
    <div style={{ width: 300, borderRight: "1px solid #f0f0f0", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Top block. Was flexShrink: 0 — right when it held only the policy
          picker, but it has since grown (carrier playbook, submission IDs,
          requested-estimate cards), and a rigid block taller than the modal
          pushed the estimates list AND the action buttons clean out of view
          with no scrollbar anywhere. Same fix, same shape, as the breakdown
          tab's sidebar: natural height, yields when there isn't room, scrolls
          what doesn't fit. */}
      <div style={{ flex: "0 1 auto", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", borderBottom: "1px solid #f0f0f0", background: "#fafbfc" }}>
        <div style={{ padding: "20px 15px 14px 15px", flexShrink: 0 }}>
          <PolicySelect
            patientPolicies={patientPolicies}
            value={selectedPolicyID}
            onChange={(value) => {
              setSelectedPolicyID(value);
              setSelectedCaseId(undefined);
            }}
            onRequestAddPolicy={onRequestAddPolicy}
            isFetchingPolicies={isFetchingPolicies}
          />
        </div>

        {!hideVerificationTools && (
          <>
            <CarrierContactBar policy={selectedPolicy} identity={officeIdentity} taskType="estimate" />
            <CarrierPortalCredential policy={selectedPolicy} officeId={officeIdProp} />
          </>
        )}

        {/* Attention surface (manual note / scrape failure). Placed here
            between the dropdown and the list so it doesn't push the
            right-pane Policy Information down and break alignment. */}
        <div style={{ padding: "0 15px 12px 15px", flexShrink: 0 }}>
          {renderAttentionSurface()}
        </div>


        {/* Who produced the selected estimate. Super admins and covering FTC
            only — the server 403s everyone else, so this simply renders nothing
            for them rather than being conditionally hidden here. */}
        {selectedCaseId ? (
          <div style={{ padding: "0 15px 12px 15px", flexShrink: 0 }}>
            <HandledByChip kind="estimate" id={selectedCaseId} />
          </div>
        ) : null}

      </div>

      {/* Flat chronological list — no "default" / "other" split because
          predeterminations have no analog of a pinned default. flex: 1 with a
          floor so the top block can never squeeze the list out entirely. */}
      <div style={{ flex: 1, overflowY: "auto", padding: 0, minHeight: 120 }}>
        {isLoadingList ? (
          <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
        ) : !selectedPolicyID ? (
          <div style={{ textAlign: "center", padding: "30px 20px", color: "#848696", fontSize: 13, lineHeight: 1.5 }}>
            {(!Array.isArray(patientPolicies) || patientPolicies.length === 0) ? (
              <>
                <div>No insurance policy on file for this patient.</div>
                <div style={{ marginTop: 4 }}>Add a policy to fetch or upload estimates.</div>
                {onRequestAddPolicy ? (
                  <Button type="primary" size="small" style={{ marginTop: 12 }} onClick={onRequestAddPolicy}>
                    + Add Policy
                  </Button>
                ) : null}
              </>
            ) : (
              "Select a policy above to view its estimates."
            )}
          </div>
        ) : casesList.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 20px", color: "#848696", fontSize: 13 }}>
            No estimates yet.
          </div>
        ) : (
          <>
            <div style={{ padding: "14px 18px 6px 18px", fontSize: 11, fontWeight: 600, letterSpacing: 1, color: "#848696", textTransform: "uppercase" }}>
              Estimates
            </div>
            <Menu
              selectedKeys={selectedCaseId ? [selectedCaseId.toString()] : []}
              mode="inline"
              style={{ borderRight: 0 }}
              onClick={({ key }) => setSelectedCaseId(parseInt(key, 10))}
            >
              {casesList.map((item) => {
                const isFailure = item.scrape_job_state === "failed" || item.scrape_job_state === "timed_out";
                const sourceLabel = SOURCE_LABELS[item.source] || null;
                const serviceLabel = formatPlainDate(item.service_date);
                const createdLabel = formatTimestamp(item.uploaded_at);
                return (
                  <Menu.Item
                    key={item.id}
                    style={{ height: "auto", padding: "12px 24px", lineHeight: "1.2" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      {/* minWidth:0 is load-bearing. A flex item defaults to min-width:auto,
                          so without it this div refuses to shrink below its longest line
                          ("Created … · Simple Upload") and pushes the actions — including
                          Delete — outside the rail, where they are clipped and unreachable.
                          The delete button was invisible for exactly this reason. */}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          ID: {item.id} {item.patient_name && `- ${item.patient_name}`}
                        </div>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                          {createdLabel ? "Created " + createdLabel : "—"}
                          {serviceLabel ? <span style={{ marginLeft: 8 }}>· Service {serviceLabel}</span> : null}
                          {sourceLabel ? <span style={{ marginLeft: 8 }}>· {sourceLabel}</span> : null}
                          {item.notes ? (
                            // A note-only row otherwise reads identically to an
                            // empty upload in this list.
                            <span style={{ marginLeft: 8, color: "#6b43b5" }}>· Note</span>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        {item.status === "accepted" ? (
                          <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        ) : isFailure ? (
                          <span style={{ color: "#cf1322", fontSize: 11, fontWeight: 600 }}>Failed</span>
                        ) : item.status === "processing" && isAgentScrappingData && agentType === "predetermination" ? (
                          <Spin size="small" />
                        ) : null}
                        {/* Row selection is handled by <Menu onClick> on the
                            parent, so every interactive child here has to stop
                            propagation or deleting would also select the row. */}
                        <span onClick={(e) => e.stopPropagation()}>
                          <Popconfirm
                            title="Delete this estimate?"
                            description="The attached PDF is removed too. Treatment plans created from it are kept."
                            okText="Delete"
                            okButtonProps={{ danger: true, loading: deletingCaseId === item.id }}
                            cancelText="Cancel"
                            onConfirm={() => handleDeleteCase(item.id)}
                          >
                            {/* No Tooltip wrapper here — nesting one inside
                                Popconfirm can swallow the trigger click, and the
                                Popconfirm itself already explains the action. */}
                            <Button
                              type="text"
                              size="small"
                              title="Delete estimate"
                              aria-label="Delete estimate"
                              icon={<DeleteOutlined />}
                              loading={deletingCaseId === item.id}
                              style={{ color: "#cf1322" }}
                            />
                          </Popconfirm>
                        </span>
                      </div>
                    </div>
                  </Menu.Item>
                );
              })}
            </Menu>
          </>
        )}
      </div>

      {/* Sticky action rail. Only the request button: results for a requested
          estimate are recorded per submission in the results pane — see the
          file header. */}
      {!hideVerificationTools && (
      <div style={{ padding: "20px 15px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 10 }}>
        <VerificationActionButton
          operationType="predetermination"
          requestNoun="Estimate"
          patientId={patientId}
          capabilityKnown={!!capabilityMatrix}
          policySelected={!!selectedPolicyID}
          hasScraper={aiSupported}
          aiTooltip={aiTooltip}
          onOpenComposer={() => {
            openComposer(patient, {
              policyId: selectedPolicyID,
              requestType: "estimate",
            });
          }}
        />
      </div>
      )}
    </div>
  );

  const renderAttentionSurface = () => (
    // Reuses BreakdownFailureCard — policy-keyed, reads the shared
    // ScrapeJob Redux store, so it surfaces predetermination failures.
    // The card's Retry button currently dispatches via the breakdown
    // path; predet-specific retry would require extracting the visual
    // shell into a shared AttentionCard. Acceptable for v1.
    <BreakdownFailureCard
      noun="estimate"
      policyId={selectedPolicyID}
      patientId={patientId}
      patientTags={(patient && patient.tags) || []}
      policyNeedsAttention={!!(selectedPolicy && selectedPolicy.needs_attention_at)}
      manualAttentionNote={selectedPolicy && selectedPolicy.attention_note}
      manualAttentionNoteSetAt={selectedPolicy && selectedPolicy.attention_note_set_at}
      manualAttentionNoteSource={selectedPolicy && selectedPolicy.attention_note_source}
      infoAsk={selectedPolicy && selectedPolicy.info_ask}
      onNoteSaved={onNoteSaved}
    />
  );

  const renderRightPaneBody = () => {
    if (selectedCase) {
      // Simple Upload = a filed PDF with no extraction/approval data. Show
      // only the attached file — skip the heavy PredeterminationDetails table
      // (which also runs all-patients / all-procedures fetches on mount).
      const isSimpleUpload = selectedCase.source === "pdf_simple";

      if (isSimpleUpload) {
        return (
          <div>
            {/* Before the document, deliberately: the note is what explains it. */}
            <EstimateNoteBlock
              caseId={selectedCase.id}
              note={selectedCase.notes}
              author={selectedCase.notes_author}
              updatedAt={selectedCase.notes_updated_at}
              onSaved={loadCases}
            />
            <p style={{ fontSize: 16, fontWeight: 600, color: "#3d2f5e", margin: "0 0 10px 0" }}>Attached File</p>
            {/* Full-size viewer rather than the 200x133 thumbnail: for a Simple
                Upload the filed PDF is the entire content of the record, so it
                has to be readable here instead of only in a new tab. */}
            <EstimatePdfPreview
              documentUrl={selectedCase.document_url}
              documentName={selectedCase.document_name}
            />
            <div style={{ marginTop: 14, fontSize: 12, color: "#8b7bb0" }}>
              Filed {formatPlainDate(selectedCase.uploaded_at) || ""} · Simple Upload (no auto-extraction)
            </div>
          </div>
        );
      }

      return (
        <div>
          <EstimateNoteBlock
            caseId={selectedCase.id}
            note={selectedCase.notes}
            author={selectedCase.notes_author}
            updatedAt={selectedCase.notes_updated_at}
            onSaved={loadCases}
          />

          {/* Per-case attached file (PDF uploads). Shown for the selected
              estimate so the user can preview + download its document. */}
          {selectedCase.document_url ? (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 16, fontWeight: 600, color: "#3d2f5e", margin: "0 0 10px 0" }}>Attached File</p>
              <PredeterminationUploadedFiles
                file={{ document: selectedCase.document_url, type: "application/pdf", document_name: selectedCase.document_name }}
              />
            </div>
          ) : null}

          {/* PredeterminationDetails fetches by batch id. A scraper-created
              batch can contain multiple Cases (one per claim returned by
              the portal). Selecting a case here loads the whole batch's
              approval view; the user can approve cases individually inside it. */}
          <PredeterminationDetails
            key={`${selectedCase.id}-${refreshTick}`}
            setSwitchToPredeterminationDetails={() => setSelectedCaseId(undefined)}
            predeterminationOrPostPayment={selectedCase.type || "predetermination"}
            setPredeterminationTreatmentID={() => {}}
            setShowPredeterminationModal={setShowInsuranceEstimatesModal}
            predeterminationTreatmentID={defaultTreatmentPlanId}
            setPredeterminationName={() => {}}
            predeterminationBatchID={selectedCase.file_batch_id}
            eligibilityData={undefined}
            setIsApproved={() => setRefreshTick((n) => n + 1)}
            insuranceID={selectedCase.file_batch_id}
            isApproved={false}
            patient={patient}
          />
        </div>
      );
    }

    return (
      // A block with a floor rather than height:100% — the submissions panel can
      // sit above this in the same scroll container now.
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, padding: 40, textAlign: "center" }}>
        <FileSearchOutlined style={{ fontSize: 64, color: "#848696", opacity: 0.2, marginBottom: 20 }} />
        <h2 style={{ color: "#848696", fontWeight: 400 }}>Insurance Estimates</h2>
        <p style={{ color: "#848696", maxWidth: 460 }}>
          Select an estimate from the sidebar to review or approve the claim details.
          Run AI Verification to pull predeterminations from the insurance portal.
        </p>
      </div>
    );
  };

  const innerPane = (
    <div style={{ display: "flex", width: "100%", height: "100%" }} data-onboarding-id="est-pane">
      {renderSidebar()}

      <div data-onboarding-id="est-pane-right" style={{ flex: 1, backgroundColor: "#fafbfc", overflowY: "auto", position: "relative", display: "flex", flexDirection: "column" }}>
        {/* Close button — hidden when embedded; the InsuranceMasterModal
            header owns Close. */}
        {!embedded && (
          <div style={{ position: "absolute", top: 15, right: 15, zIndex: 10, display: "flex", gap: 8 }}>
            <Button onClick={() => setShowInsuranceEstimatesModal(false)}>Close</Button>
          </div>
        )}

        {/* No Mark Estimate Available / Details Needed by Smilepass buttons here: an
            estimate is answered item by item with Record result (Covered, Not
            covered, Resubmit to Carrier...), and the request completes when every
            item has its answer (RequestedEstimatesPanel). */}

        {/* Attention surface now lives in the left column (between the
            policy dropdown and the estimate list) so it doesn't push the
            Policy Information panel down and break column alignment. */}
        <div style={{ padding: "20px 24px 8px 24px" }}>
          <PolicyInformationCollapse
            officeIdentity={officeIdentity}
            selectedPolicy={selectedPolicy}
            patient={patient}
            policyCollapseActiveKey={policyCollapseActiveKey}
            setPolicyCollapseActiveKey={setPolicyCollapseActiveKey}
            carrierOptions={carrierOptions}
            isLoadingCarriers={isLoadingCarriers}
            setPolicyOverrides={setPolicyOverrides}
            onPoliciesChanged={onPoliciesChanged}
          />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px 24px" }}>
          {/* What the office asked us to chase — and, once chased, what it asked
              for: the submission rows and the claim form, with the request's
              status. In the results pane rather than the 300px sidebar so the
              form can be previewed, and above the results so an FTC user arriving
              from the estimate chip sees the ask first while an office admin
              viewing a finished request sees what they submitted right next to
              what came back. Renders nothing on a policy with no request. */}
          {selectedPolicyID ? (
            <div style={{ marginBottom: 16 }}>
              <RequestedEstimatesPanel
                policyId={selectedPolicyID}
                requestType="estimate"
                refreshKey={refreshTick}
                readOnly={hideVerificationTools}
                carrierName={selectedPolicy ? selectedPolicy.insurance_name_display || selectedPolicy.insurance_name : null}
                patientId={patientId}
                onPolicyPatched={onNoteSaved}
              />
            </div>
          ) : null}
          {renderRightPaneBody()}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {embedded ? (
        innerPane
      ) : (
        <Modal
          open={showInsuranceEstimatesModal}
          width={1400}
          zIndex={1000}
          style={{ top: 12, padding: 0, paddingBottom: 0 }}
          bodyStyle={{ padding: 0, height: "115vh", maxHeight: "115vh", display: "flex", overflow: "hidden" }}
          footer={null}
          closeIcon={false}
          destroyOnClose
        >
          {innerPane}
        </Modal>
      )}


      <SelectEstimateProvidersModal
        open={showProviderPicker}
        onCancel={() => setShowProviderPicker(false)}
        onConfirm={(providerIds) => {
          setShowProviderPicker(false);
          dispatchPredetermination(providerIds);
        }}
        accounts={estimateAccounts}
        defaultSelectedIds={
          patient && patient.provider_id ? [patient.provider_id] : []
        }
        confirmLoading={pickerBusy}
      />

      {/* The request composer, stacked over this modal. */}
      {composer}
    </>
  );
}
